/**
 * Cart Sample Product Validation
 * Handles validation for sample products requiring minimum 10 items
 */

import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const MIN_SAMPLE_QUANTITY = 10;
const SAMPLE_TAG = 'sample';

class CartSampleValidation {
  constructor() {
    this.messageElement = null;
    this.modalElement = null;
    this.checkoutButton = null;
    this.cartDrawer = null;
    /** When true, sample lines are being removed; modal stays open with loader. */
    this._sampleModalRemoving = false;

    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.cartDrawer = document.querySelector('cart-drawer-component');

    this.createModal();
    document.addEventListener('sample-checkout:block-breeze', () => {
      this.checkSampleQuantity();
      this.showModal();
    });

    this.createMessageElement();
    this.attachCheckoutListener();
    this.setupMutationObserver();
    this.checkOpenDrawerAfterReload();

    // Listen for cart updates - use a delay to ensure DOM is updated
    document.addEventListener(ThemeEvents.cartUpdate, () => {
      setTimeout(() => {
        // Ensure message element exists and is positioned correctly
        this.ensureMessageElement();
        // Move message to top after a brief delay to ensure DOM is fully updated
        setTimeout(() => {
          this.moveMessageToTop();
        }, 100);
        this.checkSampleQuantity();
      }, 500);
    });

    // Initial check with delay to ensure cart is rendered
    setTimeout(() => {
      this.ensureMessageElement();
      this.checkSampleQuantity();
    }, 600);
  }

  /**
   * After a "proceed without samples" reload, re-open the cart drawer
   */
  checkOpenDrawerAfterReload() {
    if (sessionStorage.getItem('openCartDrawerAfterSampleRemoval') === 'true') {
      sessionStorage.removeItem('openCartDrawerAfterSampleRemoval');
      setTimeout(() => {
        const cartDrawer = document.querySelector('cart-drawer-component');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
      }, 500);
    }
  }

  /**
   * Sets up a MutationObserver to watch for cart drawer content changes
   */
  setupMutationObserver() {
    const cartItems = document.querySelector('.cart-drawer__items');
    if (!cartItems) {
      // Retry after a delay
      setTimeout(() => this.setupMutationObserver(), 200);
      return;
    }

    // Watch for changes to cart-drawer__items children
    this.mutationObserver = new MutationObserver((mutations) => {
      // Check if cart-items__wrapper was added/changed
      const hasCartItemsWrapper = cartItems.querySelector('.cart-items__wrapper');
      if (hasCartItemsWrapper) {
        // Ensure message is in the right place
        this.ensureMessageElement();
      }
    });

    // Observe child list changes
    this.mutationObserver.observe(cartItems, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Ensures the message element exists and is positioned correctly
   */
  ensureMessageElement() {
    // Since the message is now part of the morphed content, we just need to find it
    if (!this.messageElement || !document.contains(this.messageElement)) {
      this.createMessageElement();
    }

    // Ensure message is positioned at the top of cart-drawer__items
    if (this.messageElement) {
      const cartItems = document.querySelector('.cart-drawer__items');
      if (cartItems && this.messageElement.parentElement === cartItems) {
        // Message is already a direct child of cart-drawer__items
        // Move it to the top if it's not already there
        if (cartItems.firstChild !== this.messageElement) {
          cartItems.insertBefore(this.messageElement, cartItems.firstChild);
        }
      } else if (cartItems) {
        // Message is not a direct child, move it to be the first child
        const cartItemsWrapper = cartItems.querySelector('.cart-items__wrapper');
        if (cartItemsWrapper && this.messageElement.parentElement) {
          // Remove from current parent
          this.messageElement.parentElement.removeChild(this.messageElement);
          // Insert at the top of cart-drawer__items
          cartItems.insertBefore(this.messageElement, cartItems.firstChild);
        }
      }
    }
  }

  /**
   * Counts sample products in the cart
   * @returns {number} Total quantity of sample products
   */
  countSampleProducts() {
    const readQtyFromHost = (host) => {
      if (!(host instanceof HTMLElement)) return null;
      const raw = host.getAttribute('data-cart-sample-quantity');
      if (raw === null || raw === '') return null;
      const n = parseInt(raw, 10);
      return Number.isNaN(n) ? null : n;
    };

    const hosts = [
      document.querySelector('cart-drawer-component cart-items-component[data-cart-sample-quantity]'),
      document.querySelector('main cart-items-component[data-cart-sample-quantity]'),
      document.querySelector('cart-items-component[data-cart-sample-quantity]'),
    ];
    let fromAttr = null;
    for (const host of hosts) {
      const v = readQtyFromHost(host);
      if (v !== null) {
        fromAttr = v;
        break;
      }
    }

    const cartItems = document.querySelectorAll('[data-sample-product="true"]');
    let fromRows = 0;

    cartItems.forEach((item) => {
      const quantityInput = item.querySelector(
        'input[type="number"][name*="updates"], input[type="number"][data-cart-line], .quantity-selector input[type="number"]'
      );
      if (quantityInput) {
        const quantity = parseInt(quantityInput.value, 10) || 0;
        fromRows += quantity;
      }
    });

    if (cartItems.length > 0) {
      return fromRows > 0 ? fromRows : fromAttr ?? 0;
    }
    return fromAttr ?? 0;
  }

  /**
   * True when the cart contains samples but fewer than the minimum (checkout must be blocked).
   * Used by theme Breeze integration so it does not open checkout behind the sample modal.
   */
  shouldBlockCheckout() {
    const sampleCount = this.countSampleProducts();
    return sampleCount > 0 && sampleCount < MIN_SAMPLE_QUANTITY;
  }

  /**
   * Checks if cart has sufficient sample products and updates UI
   */
  checkSampleQuantity() {
    // Ensure message element exists and is positioned correctly
    this.ensureMessageElement();

    const sampleCount = this.countSampleProducts();
    const hasInsufficientSamples = sampleCount > 0 && sampleCount < MIN_SAMPLE_QUANTITY;

    if (this.messageElement) {
      if (hasInsufficientSamples) {
        this.messageElement.classList.remove('hidden');
        this.messageElement.textContent = 'Please add at least 10 sample products to proceed.';
      } else {
        this.messageElement.classList.add('hidden');
      }
    }
  }

  /**
   * Creates the persistent message element in cart drawer
   */
  createMessageElement() {
    // Find the message element that's now part of the cart-products snippet
    // This element will persist through morphing since it's in the morphed content
    let messageEl = document.querySelector('[data-sample-message]');

    if (!messageEl) {
      // If not found, try to find it by class (fallback)
      messageEl = document.querySelector('.cart-sample-message');
    }

    if (!messageEl) {
      // If still not found, the cart drawer might not be ready yet
      // Retry after a short delay
      setTimeout(() => this.createMessageElement(), 100);
      return;
    }

    this.messageElement = messageEl;
    // Move to top immediately after finding it
    this.moveMessageToTop();
  }

  /**
   * Moves the message element to the top of cart-drawer__items
   */
  moveMessageToTop() {
    if (!this.messageElement || !document.contains(this.messageElement)) {
      return;
    }

    const cartItems = document.querySelector('.cart-drawer__items');
    if (!cartItems) {
      return;
    }

    // If message is already a direct child and is first, we're done
    if (this.messageElement.parentElement === cartItems && cartItems.firstChild === this.messageElement) {
      return;
    }

    // Remove message from its current parent
    if (this.messageElement.parentElement) {
      this.messageElement.parentElement.removeChild(this.messageElement);
    }

    // Insert at the very top of cart-drawer__items
    cartItems.insertBefore(this.messageElement, cartItems.firstChild);
  }

  /**
   * Creates the checkout restriction modal (default + loading panels).
   */
  createModal() {
    let modal = document.getElementById('sample-checkout-modal');
    if (modal && !modal.querySelector('[data-sample-modal-panel="loading"]')) {
      modal.remove();
      modal = null;
    }
    if (modal) {
      this.modalElement = modal;
      return;
    }

    modal = document.createElement('dialog');
    modal.id = 'sample-checkout-modal';
    modal.className = 'sample-checkout-modal';
    modal.setAttribute('aria-labelledby', 'sample-modal-title');
    modal.setAttribute('aria-describedby', 'sample-modal-description');

    modal.innerHTML = `
      <div class="sample-checkout-modal__content">
        <div class="sample-checkout-modal__panel sample-checkout-modal__panel--default" data-sample-modal-panel="default">
          <h2 id="sample-modal-title" class="sample-checkout-modal__title">
            At least 10 sample products are required.
          </h2>
          <p id="sample-modal-description" class="sample-checkout-modal__description">
            Would you like to proceed without sample products?
          </p>
          <div class="sample-checkout-modal__actions">
            <button
              type="button"
              class="button sample-checkout-modal__button sample-checkout-modal__button--primary"
              data-action="proceed-without-samples"
            >
              Yes, proceed without samples
            </button>
            <button
              type="button"
              class="button sample-checkout-modal__button sample-checkout-modal__button--secondary"
              data-action="add-samples"
            >
              No, add samples
            </button>
          </div>
        </div>
        <div
          class="sample-checkout-modal__panel sample-checkout-modal__panel--loading"
          data-sample-modal-panel="loading"
          hidden
          aria-live="polite"
          aria-busy="false"
        >
          <div class="sample-checkout-modal__spinner" aria-hidden="true"></div>
          <p class="sample-checkout-modal__loading-text">Removing sample products…</p>
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    this.modalElement = modal;

    modal.querySelector('[data-action="proceed-without-samples"]')?.addEventListener('click', () =>
      this.handleProceedWithoutSamples()
    );

    modal.querySelector('[data-action="add-samples"]')?.addEventListener('click', () => this.handleAddSamples());

    modal.addEventListener('click', (e) => {
      if (this._sampleModalRemoving) return;
      if (e.target === modal) {
        this.closeModal();
      }
    });

    modal.addEventListener('keydown', (e) => {
      if (this._sampleModalRemoving) return;
      if (e.key === 'Escape') {
        this.closeModal();
      }
    });
  }

  /**
   * Toggles between choice UI and loading state while samples are removed.
   */
  applySampleModalLoadingState(loading) {
    this._sampleModalRemoving = loading;
    if (!this.modalElement) return;

    const defaultPanel = this.modalElement.querySelector('[data-sample-modal-panel="default"]');
    const loadingPanel = this.modalElement.querySelector('[data-sample-modal-panel="loading"]');

    if (loading) {
      this.modalElement.setAttribute('aria-busy', 'true');
      defaultPanel?.setAttribute('hidden', '');
      loadingPanel?.removeAttribute('hidden');
      if (loadingPanel) loadingPanel.setAttribute('aria-busy', 'true');
      const loadMsg = loadingPanel?.querySelector('.sample-checkout-modal__loading-text');
      if (loadMsg instanceof HTMLElement) {
        loadMsg.setAttribute('tabindex', '-1');
        loadMsg.focus({ preventScroll: true });
      }
    } else {
      this.modalElement.removeAttribute('aria-busy');
      defaultPanel?.removeAttribute('hidden');
      loadingPanel?.setAttribute('hidden', '');
      if (loadingPanel) loadingPanel.setAttribute('aria-busy', 'false');
    }

    this.modalElement.querySelectorAll('button').forEach((btn) => {
      btn.disabled = loading;
    });
  }

  /**
   * Shows the modal
   */
  showModal() {
    if (this.modalElement) {
      this.applySampleModalLoadingState(false);
      this.modalElement.showModal();
      const firstButton = this.modalElement.querySelector('[data-action="proceed-without-samples"]');
      if (firstButton instanceof HTMLElement) {
        firstButton.focus();
      }
    }
  }

  /**
   * Closes the modal
   */
  closeModal() {
    if (this.modalElement) {
      this.applySampleModalLoadingState(false);
      this.modalElement.close();
    }
  }

  /**
   * 1-based line numbers for Shopify cart/change.js, highest first (avoids index shift bugs).
   * data-line-index is Liquid item.index (0-based).
   */
  getSampleLineNumbersOneBasedFromDom() {
    const sampleRows = document.querySelectorAll('[data-sample-product="true"]');
    const lines = [];
    sampleRows.forEach((el) => {
      const raw = el.getAttribute('data-line-index');
      if (raw === null || raw === '') return;
      const zeroBased = parseInt(raw, 10);
      if (Number.isNaN(zeroBased)) return;
      lines.push(zeroBased + 1);
    });
    return [...new Set(lines)].filter((n) => n > 0).sort((a, b) => b - a);
  }

  /**
   * When DOM has no sample rows (stale markup): lines with _sample line-item property.
   */
  getSampleLineNumbersOneBasedFromCart(cartData) {
    if (!cartData?.items?.length) return [];
    const lines = [];
    cartData.items.forEach((item, i) => {
      const p = item.properties;
      if (p && String(p._sample).toLowerCase() === 'true') {
        lines.push(i + 1);
      }
    });
    return [...new Set(lines)].sort((a, b) => b - a);
  }

  async removeSampleLinesSequentially(linesDesc) {
    for (const line of linesDesc) {
      const body = JSON.stringify({ line, quantity: 0 });
      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Cart change failed (${response.status}): ${text}`);
      }
      await response.json();
    }
  }

  /**
   * After samples are removed: open Breeze checkout with remaining cart, or theme fallbacks.
   */
  startBreezeOrFallbackCheckout() {
    this.forceCleanupState();
    if (window.breeze && typeof window.breeze.startCheckout === 'function') {
      window.breeze.startCheckout();
      return;
    }
    this.triggerCheckout();
  }

  /**
   * Handles "Yes, proceed without samples": remove all sample lines, then go to Breeze (or Shiprocket /checkout).
   */
  async handleProceedWithoutSamples() {
    if (this._sampleModalRemoving) return;

    this.applySampleModalLoadingState(true);

    try {
      let linesDesc = this.getSampleLineNumbersOneBasedFromDom();

      if (linesDesc.length === 0) {
        const cartPeek = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) =>
          r.json()
        );
        linesDesc = this.getSampleLineNumbersOneBasedFromCart(cartPeek);
      }

      if (linesDesc.length === 0) {
        sessionStorage.setItem('openCartDrawerAfterSampleRemoval', 'true');
        window.location.reload();
        return;
      }

      await this.removeSampleLinesSequentially(linesDesc);

      const cartData = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );
      document.dispatchEvent(
        new CartUpdateEvent(cartData, 'cart-sample-validation', {
          source: 'cart-sample-validation-remove-samples',
        })
      );

      this.closeModal();
      this.closeCartDrawer();
      await new Promise((r) => setTimeout(r, 200));
      this.startBreezeOrFallbackCheckout();
    } catch (error) {
      console.error('Error removing sample products:', error);
      this.applySampleModalLoadingState(false);
      this.forceCleanupState();
      alert('There was an error removing sample products. Please try again.');
    }
  }

  /**
   * Checks and removes freebies based on current cart total
   */
  async checkAndRemoveFreebies() {
    const freebieManager = window.cartFreebieManager;
    if (!freebieManager) {
      console.warn('[Sample Validation] Freebie manager not found, skipping freebie check');
      return;
    }

    try {
      // Call the freebie manager's check function directly
      await freebieManager.checkAndManageFreebies();

      // Wait for freebie manager to finish processing
      let attempts = 0;
      const maxAttempts = 30; // 3 seconds max wait
      while (freebieManager.isProcessing && attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }

      // Additional delay to ensure all DOM updates are complete
      await new Promise(resolve => setTimeout(resolve, 300));

    } catch (error) {
      console.error('[Sample Validation] Error checking freebies:', error);
      // Don't block checkout if freebie check fails
    }
  }


  /**
   * Cleans up all lingering freebie manager / checkout state, then triggers checkout.
   * This prevents the page from freezing after Shiprocket checkout opens.
   */
  cleanupAndCheckout() {
    this.forceCleanupState();
    this.triggerCheckout();
  }

  /**
   * Forces cleanup of freebie manager state (disabled buttons, event listeners, flags)
   */
  forceCleanupState() {
    const freebieManager = window.cartFreebieManager;
    if (freebieManager) {
      freebieManager.isValidatingCheckout = false;
      freebieManager.isProcessing = false;
      freebieManager.enableCheckoutButton();
    }

    // Also clean up cart form flag directly
    const cartForm = document.getElementById('cart-form');
    if (cartForm) {
      delete cartForm.dataset.freebieRemovalActive;
    }
  }

  /**
   * Properly closes the cart drawer dialog and removes the body scroll lock.
   * We close it directly instead of clicking the close button because
   * the async closeDialog() can get stuck waiting for animations.
   */
  closeCartDrawer() {
    const cartDrawerComponent = document.querySelector('cart-drawer-component');
    if (cartDrawerComponent) {
      const dialog = cartDrawerComponent.querySelector('dialog');
      if (dialog && dialog.open) {
        // Restore body scroll by removing the fixed position styles
        // that DialogComponent.showDialog() sets
        document.body.style.width = '';
        document.body.style.position = '';
        document.body.style.top = '';
        // Close the dialog element directly
        dialog.close();
        dialog.classList.remove('dialog-closing');
      }
    }

    // Also close the sample checkout modal if still open
    const sampleModal = document.getElementById('sample-checkout-modal');
    if (sampleModal && sampleModal.open) {
      sampleModal.close();
    }
  }

  /**
   * Triggers checkout via Shiprocket or fallback to standard checkout
   */
  triggerCheckout() {
    // Close the cart drawer properly before triggering Shiprocket checkout
    this.closeCartDrawer();

    // Set bypass flag so the freebie manager's click handler won't intercept
    const freebieManager = window.cartFreebieManager;
    if (freebieManager) {
      freebieManager.isValidatingCheckout = true;
    }

    const srButton = document.querySelector('.sr-headless-checkout');
    if (srButton) {
      // Click the actual button so Shiprocket gets a proper event context
      srButton.click();
      // Reset the bypass flag after a short delay
      setTimeout(() => {
        if (freebieManager) {
          freebieManager.isValidatingCheckout = false;
        }
      }, 200);
      return;
    }

    // Fallback to standard checkout
    window.location.href = '/checkout';
  }

  /**
   * Handles "No, add samples" action
   */
  handleAddSamples() {
    this.closeModal();
    // Ensure cart drawer stays open
    if (this.cartDrawer && !this.cartDrawer.refs.dialog?.open) {
      this.cartDrawer.open();
    }
  }

  /**
   * Attaches listener to checkout button
   */
  attachCheckoutListener() {
    // Intercept form submission
    const cartForm = document.getElementById('cart-form');
    if (cartForm) {
      cartForm.addEventListener('submit', (e) => {
        if (
          typeof window.__cartSecretOfferOnlyBlocksCheckout === 'function' &&
          window.__cartSecretOfferOnlyBlocksCheckout()
        ) {
          const submitter = e.submitter;
          if (submitter instanceof HTMLButtonElement && submitter.disabled) {
            return false;
          }
          e.preventDefault();
          e.stopPropagation();
          if (typeof window.__clearCartSecretOfferOnly === 'function') {
            window.__clearCartSecretOfferOnly();
          }
          return false;
        }

        const sampleCount = this.countSampleProducts();

        // Only check if there are sample products in cart
        if (sampleCount > 0 && sampleCount < MIN_SAMPLE_QUANTITY) {
          e.preventDefault();
          e.stopPropagation();
          this.showModal();
          return false;
        }
      }, true);
    }

    // Also intercept button clicks as backup (including Shiprocket checkout button)
    document.addEventListener('click', (e) => {
      const checkoutButton = e.target.closest('#checkout, .cart__checkout-button[data-checkout-button="true"], .sr-headless-checkout');
      if (!checkoutButton) return;

      if (
        typeof window.__cartSecretOfferOnlyBlocksCheckout === 'function' &&
        window.__cartSecretOfferOnlyBlocksCheckout()
      ) {
        if (checkoutButton instanceof HTMLButtonElement && checkoutButton.disabled) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        if (typeof window.__clearCartSecretOfferOnly === 'function') {
          window.__clearCartSecretOfferOnly();
        }
        return false;
      }

      const sampleCount = this.countSampleProducts();

      // Only check if there are sample products in cart
      if (sampleCount > 0 && sampleCount < MIN_SAMPLE_QUANTITY) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.showModal();
        return false;
      }
    }, true); // Use capture phase to intercept before form submission
  }
}

// Initialize when module loads
const cartSampleValidation = new CartSampleValidation();

/** Breeze (theme.liquid) must consult this before startCheckout so the sample modal is not bypassed. */
window.__cartSampleValidationShouldBlock = () => cartSampleValidation.shouldBlockCheckout();

/** Called from theme.liquid when Breeze is blocked so the modal always opens (listener order / load timing). */
window.__showSampleCheckoutModal = () => {
  cartSampleValidation.checkSampleQuantity();
  cartSampleValidation.showModal();
};

export default cartSampleValidation;

